import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import type { Session } from '@supabase/supabase-js';
import { signInWithDiscord, signOut as supabaseSignOut } from '@/lib/supabase/auth';
import { supabase } from '@/lib/supabase/client';
import { completeProfileOnboardingRpc } from '@/lib/supabase/rpc';
import type { AppRole, Profile } from '@/types/domain';
import { hasProfileInGameName } from '@/utils/profile';

interface AuthContextValue {
  authMode: 'mock' | 'supabase';
  profile: Profile | null;
  session: Session | null;
  isLoading: boolean;
  isSignedIn: boolean;
  isApproved: boolean;
  needsOnboarding: boolean;
  hasAnyRole: (roles: AppRole[]) => boolean;
  refreshProfile: () => Promise<void>;
  completeOnboarding: (inGameName: string) => Promise<void>;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  previewPending: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function createProfileFromSession(session: Session): Profile {
  const metadata = session.user.user_metadata;
  const roles = Array.isArray(metadata.guild_roles) ? metadata.guild_roles : ['member'];

  return {
    id: session.user.id,
    discordId: metadata.provider_id ?? metadata.sub ?? session.user.id,
    discordUsername: metadata.preferred_username ?? metadata.full_name ?? metadata.name ?? 'discord-user',
    displayName: metadata.display_name ?? metadata.full_name ?? metadata.name ?? 'Guild Member',
    inGameName: metadata.in_game_name ?? undefined,
    avatarUrl: metadata.avatar_url,
    approvalStatus: metadata.guild_approval ?? 'pending',
    roles,
    badges: [],
    dmMassAlertsEnabled: true,
    dmNotificationsPaused: false,
    createdAt: session.user.created_at,
  };
}

async function loadProfileForSession(session: Session): Promise<Profile> {
  if (!supabase) {
    return createProfileFromSession(session);
  }

  const [{ data: profileRow }, { data: roleRows }, { data: profileBadgeRows }] = await Promise.all([
    supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .maybeSingle(),
    supabase.from('profile_roles').select('role').eq('profile_id', session.user.id),
    supabase
      .from('profile_badges')
      .select('created_at, assigned_by, note, badge:guild_badges(id, slug, display_name, description, accent_color, is_active)')
      .eq('profile_id', session.user.id),
  ]);

  if (!profileRow) {
    return createProfileFromSession(session);
  }

  const roles = (roleRows?.map((row) => row.role) ?? ['member']) as AppRole[];

  return {
    id: profileRow.id,
    discordId: profileRow.discord_user_id ?? session.user.id,
    discordUsername:
      profileRow.discord_username ??
      session.user.user_metadata.preferred_username ??
      session.user.user_metadata.name ??
      'discord-user',
    displayName: profileRow.display_name,
    inGameName: profileRow.in_game_name ?? undefined,
    avatarUrl: profileRow.avatar_url ?? undefined,
    approvalStatus: profileRow.approval_status,
    roles,
    notes: profileRow.notes ?? undefined,
    progressionLevelOverride: profileRow.progression_level_override ?? undefined,
    chatMuted: profileRow.chat_muted,
    chatMutedReason: profileRow.chat_muted_reason ?? undefined,
    chatMutedAt: profileRow.chat_muted_at ?? undefined,
    chatMutedBy: profileRow.chat_muted_by ?? undefined,
    dmMassAlertsEnabled: profileRow.dm_mass_alerts_enabled,
    dmNotificationsPaused: profileRow.dm_notifications_paused,
    dmLastSentAt: profileRow.dm_last_sent_at ?? undefined,
    dmLastError: profileRow.dm_last_error ?? undefined,
    badges:
      profileBadgeRows?.flatMap((row) => {
        const badge = Array.isArray(row.badge) ? row.badge[0] : row.badge;

        if (!badge || badge.is_active === false) {
          return [];
        }

        return [
          {
            profileId: profileRow.id,
            badgeId: badge.id,
            slug: badge.slug,
            displayName: badge.display_name,
            description: badge.description ?? undefined,
            accentColor: badge.accent_color,
            assignedBy: row.assigned_by ?? undefined,
            note: row.note ?? undefined,
            createdAt: row.created_at,
          },
        ];
      }) ?? [],
    createdAt: profileRow.created_at,
    approvedAt: profileRow.approved_at ?? undefined,
  };
}

export function AuthProvider({ children }: PropsWithChildren): JSX.Element {
  const [session, setSession] = useState<Session | null>(null);
  const [supabaseProfile, setSupabaseProfile] = useState<Profile | null>(null);
  const [mockProfile, setMockProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(supabase));

  useEffect(() => {
    if (!supabase) {
      setIsLoading(false);
      return undefined;
    }

    let isActive = true;

    async function syncSession(nextSession: Session | null): Promise<void> {
      if (!isActive) {
        return;
      }

      setSession(nextSession);

      if (!nextSession) {
        setSupabaseProfile(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const nextProfile = await loadProfileForSession(nextSession);

        if (!isActive) {
          return;
        }

        setSupabaseProfile(nextProfile);
      } catch (_error) {
        if (!isActive) {
          return;
        }

        setSupabaseProfile(createProfileFromSession(nextSession));
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }

    supabase.auth.getSession().then(({ data }) => {
      void syncSession(data.session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      void syncSession(nextSession);
    });

    return () => {
      isActive = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!supabase || !session) {
      return undefined;
    }

    const client = supabase;
    let isActive = true;

    const refreshCurrentProfile = async () => {
      try {
        const nextProfile = await loadProfileForSession(session);

        if (isActive) {
          setSupabaseProfile(nextProfile);
        }
      } catch {
        if (isActive) {
          setSupabaseProfile(createProfileFromSession(session));
        }
      }
    };

    const channel = client
      .channel(`profile-sync-${session.user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles', filter: `id=eq.${session.user.id}` }, refreshCurrentProfile)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'profile_roles', filter: `profile_id=eq.${session.user.id}` },
        refreshCurrentProfile,
      )
      .subscribe();

    return () => {
      isActive = false;
      void client.removeChannel(channel);
    };
  }, [session]);

  const authMode = supabase ? 'supabase' : 'mock';
  const profile = authMode === 'supabase' ? supabaseProfile : mockProfile;
  const needsOnboarding = Boolean(profile) && !hasProfileInGameName(profile);

  const value = useMemo<AuthContextValue>(
    () => ({
      authMode,
      profile,
      session,
      isLoading,
      isSignedIn: authMode === 'supabase' ? Boolean(session) : Boolean(profile),
      isApproved: profile?.approvalStatus === 'approved',
      needsOnboarding,
      hasAnyRole: (roles) =>
        roles.some((role) => profile?.roles.includes(role) || (role === 'admin' && Boolean(profile?.roles.includes('owner')))),
      refreshProfile: async () => {
        if (authMode !== 'supabase' || !session) {
          return;
        }

        setIsLoading(true);

        try {
          const nextProfile = await loadProfileForSession(session);
          setSupabaseProfile(nextProfile);
        } catch (_error) {
          setSupabaseProfile(createProfileFromSession(session));
        } finally {
          setIsLoading(false);
        }
      },
      completeOnboarding: async (inGameName) => {
        const nextInGameName = inGameName.trim();

        if (!nextInGameName) {
          throw new Error('In-Game Name is required.');
        }

        if (authMode !== 'supabase') {
          setMockProfile((currentProfile) =>
            currentProfile
              ? {
                  ...currentProfile,
                  inGameName: nextInGameName,
                }
              : currentProfile,
          );
          return;
        }

        setIsLoading(true);

        try {
          const { error } = await completeProfileOnboardingRpc(nextInGameName);

          if (error) {
            throw new Error(error.message);
          }

          if (session) {
            try {
              const nextProfile = await loadProfileForSession(session);
              setSupabaseProfile(nextProfile);
            } catch {
              setSupabaseProfile((currentProfile) =>
                currentProfile
                  ? {
                      ...currentProfile,
                      inGameName: nextInGameName,
                    }
                  : {
                      ...createProfileFromSession(session),
                      inGameName: nextInGameName,
                    },
              );
            }
          }
        } finally {
          setIsLoading(false);
        }
      },
      login: async () => {
        if (authMode === 'supabase') {
          await signInWithDiscord();
          return;
        }

        const { demoProfiles } = await import('@/data/demoData');
        setMockProfile(demoProfiles.lyka);
      },
      logout: async () => {
        if (authMode === 'supabase') {
          await supabaseSignOut();
          return;
        }

        setMockProfile(null);
      },
      previewPending: () => {
        void import('@/data/demoData').then(({ demoProfiles }) => {
          setMockProfile(demoProfiles.nova);
        });
      },
    }),
    [authMode, isLoading, needsOnboarding, profile, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}
